import { create } from 'zustand';
import { getState, useState } from '@/framework';
import { IExportState, StateType, type ILayerProps } from '@/version-types';

/** 状态 */
interface IState {
    isCropImage: boolean;
    isAutoCutoutLayer: boolean;
    isOperation: boolean;
    currentLayer: ILayerProps | null;
    isApplying: boolean;
}

/** 动作 */
interface IAction {
    setIsCropImage: (isCropImage: boolean) => void;
    setIsAutoCutoutLayer: (isAutoCutoutLayer: boolean) => void;
    setIsOperation: (isOperation: boolean) => void;
    setCurrentLayer: (currentLayer: ILayerProps | null) => void;
    setIsApplying: (isApplying: boolean) => void;
    reset: () => void;
}

/** 存储类型 */
interface IStore extends IState, IAction {}

/** 初始化状态 */
export function getInitialState() {
    return {
        isCropImage: false,
        isAutoCutoutLayer: true,
        isOperation: false,
        currentLayer: null,
        isApplying: false,
    };
}

/** 创建存储 */
const createState = () => {
    const store = create<IStore>((set) => ({
        ...getInitialState(),

        setIsCropImage: (isCropImage) => {
            set({ isCropImage });
        },
        setIsAutoCutoutLayer: (isAutoCutoutLayer) => {
            set({ isAutoCutoutLayer });
        },
        setIsOperation: (isOperation) => {
            set({ isOperation });
        },
        setCurrentLayer: (currentLayer) => {
            set({ currentLayer });
        },
        setIsApplying: (isApplying) => {
            set({ isApplying });
        },
        reset: () => {
            set(getInitialState());
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'crop-img-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useCropImgState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getCropImgState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

/**
 * 监听裁剪插件状态变化
 *
 * @param identity - 编辑器实例标识
 * @param callback - 状态变化回调
 * @returns 取消监听函数
 */
export const watchCropImgState = (identity: string, callback: (state: IStore) => void) => {
    return getState<ModuleType>(identity)[name].subscribe((state, previousState) => {
        if (state.currentLayer !== previousState.currentLayer || state.isApplying !== previousState.isApplying) {
            callback(state);
        }
    });
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
